import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import api from "../../../api";
import { IMAGE_URL } from "../../../path";
import "../../../styles/Login.scss";

interface ProductSize {
    id: number;
    product_id: number;
    title: string;
    capacity: string;
    pack_shot: string | null;
    active: boolean;
    display_order: number;
}

interface ProductSizeModalProps {
    productId: number;
    size?: ProductSize | null;
    onClose: () => void;
    onSaved: () => void;
}

const ProductSizeModal: React.FC<ProductSizeModalProps> = ({
                                                               productId,
                                                               size,
                                                               onClose,
                                                               onSaved,
                                                           }) => {
    const isEditing = !!size;

    const [title, setTitle] = useState(size?.title ?? "");
    const [capacity, setCapacity] = useState(size?.capacity ?? "");
    const [active, setActive] = useState(size?.active ?? true);
    const [displayOrder, setDisplayOrder] = useState(
        size?.display_order ?? 0
    );

    const [packShot, setPackShot] = useState<File | null>(null);
    const [existingPackShot, setExistingPackShot] = useState<string | null>(
        size?.pack_shot ?? null
    );

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        setTitle(size?.title ?? "");
        setCapacity(size?.capacity ?? "");
        setActive(size?.active ?? true);
        setDisplayOrder(size?.display_order ?? 0);
        setPackShot(null);
        setExistingPackShot(size?.pack_shot ?? null);
        setError("");
    }, [size]);

    const handlePackShotChange = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const file = e.target.files?.[0] ?? null;

        setPackShot(file);

        if (file) {
            setExistingPackShot(null);
        }
    };

    const getImageUrl = (path: string | null) => {
        if (!path) {
            return "";
        }

        if (
            path.startsWith("http://") ||
            path.startsWith("https://")
        ) {
            return path;
        }

        return `${IMAGE_URL}/${path}`;
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        setError("");

        if (!title.trim()) {
            setError("Size title is required.");
            return;
        }

        if (!capacity.trim()) {
            setError("Capacity is required.");
            return;
        }

        try {
            setLoading(true);

            const formData = new FormData();

            formData.append("product_id", String(productId));
            formData.append("title", title.trim());
            formData.append("capacity", capacity.trim());
            formData.append("active", active ? "1" : "0");
            formData.append(
                "display_order",
                String(Number(displayOrder))
            );

            if (packShot) {
                formData.append("pack_shot", packShot);
            }

            if (isEditing && size) {
                formData.append("_method", "PUT");

                await api.post(
                    `/admin/product-sizes/${size.id}`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    }
                );
            } else {
                await api.post(
                    `/admin/products/${productId}/sizes`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    }
                );
            }

            onSaved();
        } catch (err: any) {
            console.error(err);

            if (err.response?.data?.errors) {
                const validationErrors =
                    err.response.data.errors;

                const firstError =
                    Object.values(validationErrors)[0];

                if (Array.isArray(firstError)) {
                    setError(String(firstError[0]));
                } else {
                    setError(String(firstError));
                }
            } else {
                setError(
                    err.response?.data?.message ||
                    "Failed to save product size."
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-modal-overlay">
            <div className="admin-modal admin-product-size-modal">
                <div className="admin-modal-header">
                    <h2>
                        {isEditing
                            ? "Edit Product Size"
                            : "Add Product Size"}
                    </h2>

                    <button
                        type="button"
                        className="admin-modal-close"
                        onClick={onClose}
                        disabled={loading}
                    >
                        ×
                    </button>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className="admin-modal-content"
                >
                    {error && (
                        <div className="form-error">
                            {error}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="product-size-title">
                            Title
                        </label>

                        <input
                            id="product-size-title"
                            type="text"
                            value={title}
                            onChange={(e) =>
                                setTitle(e.target.value)
                            }
                            placeholder="e.g. Safari Vinegar 500ml"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="product-size-capacity">
                            Capacity
                        </label>

                        <input
                            id="product-size-capacity"
                            type="text"
                            value={capacity}
                            onChange={(e) =>
                                setCapacity(e.target.value)
                            }
                            placeholder="e.g. 500ml"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="product-size-display-order">
                            Display Order
                        </label>

                        <input
                            id="product-size-display-order"
                            type="number"
                            min="0"
                            value={displayOrder}
                            onChange={(e) =>
                                setDisplayOrder(
                                    Number(e.target.value)
                                )
                            }
                        />
                    </div>

                    <div className="form-group">
                        <label className="admin-checkbox-label">
                            <input
                                type="checkbox"
                                checked={active}
                                onChange={(e) =>
                                    setActive(
                                        e.target.checked
                                    )
                                }
                            />

                            <span>Active</span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label htmlFor="product-size-pack-shot">
                            Pack Shot
                        </label>

                        <input
                            id="product-size-pack-shot"
                            type="file"
                            accept="image/*"
                            onChange={handlePackShotChange}
                        />

                        {(existingPackShot || packShot) && (
                            <div className="admin-product-pack-shot-preview-container">
                                {packShot ? (
                                    <img
                                        src={URL.createObjectURL(
                                            packShot
                                        )}
                                        alt="New pack shot"
                                        className="admin-product-pack-shot-preview"
                                    />
                                ) : existingPackShot ? (
                                    <img
                                        src={getImageUrl(
                                            existingPackShot
                                        )}
                                        alt={title}
                                        className="admin-product-pack-shot-preview"
                                    />
                                ) : null}
                            </div>
                        )}
                    </div>

                    <div className="admin-modal-actions">
                        <button
                            type="button"
                            className="admin-table-button"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="admin-primary-button"
                            disabled={loading}
                        >
                            {loading
                                ? "Saving..."
                                : "Save Changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProductSizeModal;
